import EventDetailModal from 'components/EventDetailModal'
import { addMonths, format, getDaysInMonth, startOfMonth, parse, addDays } from 'date-fns'
import { ICalendarEvent, ICalendarEventParticipant, ICalendarEventType } from 'interface/calendar'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { fetchCalendarEventParticipants, fetchCalendarEvents } from 'services/calendar'
import { getCalendarEventTypeColors } from 'utils/color'
import { convert24HourTo12Hour } from 'utils/convertHour'
import { v4 as uuidv4 } from 'uuid'

interface IItem {
  day: number
  month: number
  year: number
}

interface CalendarMonthViewProps {
  officeId: number
  activeDate: Date
  eventTypes: ICalendarEventType[]
  onEventSuccess: (success: string) => void
}

const CalendarMonthView: FC<CalendarMonthViewProps> = ({
  officeId,
  activeDate,
  eventTypes,
  onEventSuccess,
}) => {
  const [events, setEvents] = useState<Record<string, ICalendarEvent[]>>({})

  const [isEventDetailOpened, setIsEventDetailOpened] = useState(false)
  const [isEventDetailLoading, setIsEventDetailLoading] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null)
  const [selectedEventDate, setSelectedEventDate] = useState<Date | null>(null)
  const [selectedEventParticipants, setSelectedEventParticipants] = useState<ICalendarEventParticipant[]>([])

  const items = useMemo<IItem[]>(() => {
    const currentYear = activeDate.getFullYear()
    const currentMonth = activeDate.getMonth()
    const daysInMonth = getDaysInMonth(activeDate)
    const daysInPreviousMonth = getDaysInMonth(addMonths(activeDate, -1))
    const previousMonthStart = daysInPreviousMonth - startOfMonth(activeDate).getDay() + 1

    const items: IItem[] = []
  
    // Add days from the previous month
    for (let i = previousMonthStart; i <= daysInPreviousMonth; i++) {
      items.push({ day: i, month: currentMonth - 1 < 0 ? 11 : currentMonth - 1, year: currentMonth - 1 < 0 ? currentYear - 1 : currentYear })
    }
  
    // Add days from the current month
    for (let i = 1; i <= daysInMonth; i++) {
      items.push({ day: i, month: currentMonth, year: currentYear })
    }
  
    // Add days from the next month
    let nextMonthDay = 1
    while (items.length % 7 !== 0) {
      items.push({ day: nextMonthDay, month: currentMonth + 1 > 11 ? 0 : currentMonth + 1, year: currentMonth + 1 > 11 ? currentYear + 1 : currentYear })
      nextMonthDay++
    }
    return items
  }, [activeDate])

  const isCurrentMonth = useCallback((item: IItem) => {
    return item.month === activeDate.getMonth()
  }, [activeDate])

  const isCurrentDate = (item: IItem) => {
    const currentDate = new Date()
    return item.day === currentDate.getDate() && item.month === currentDate.getMonth() && item.year === currentDate.getFullYear()
  }

  const getItemEvents = useCallback((item: IItem) => {
    return events[`${item.year}-${(item.month + 1).toString().padStart(2, '0')}-${item.day.toString().padStart(2, '0')}`]
      ?.filter((event) => eventTypes.map((type: ICalendarEventType) => type.id).includes(event.event_type))
      ?.sort((eventA, eventB) => eventA.start_at.localeCompare(eventB.start_at)) ?? []
  }, [events, eventTypes])

  useEffect(() => {
    fetchEvents()
    // eslint-disable-next-line
  }, [items, officeId])

  const fetchEvents = () => {
    const startDate = parse(`${items[0].year}-${(items[0].month + 1).toString().padStart(2, '0')}-${items[0].day.toString().padStart(2, '0')}`, 'yyyy-MM-dd', new Date())
    const startDateStr = format(startDate, 'yyyy-MM-dd')
    const endDateStr = format(addDays(startDate, items.length), 'yyyy-MM-dd')
    fetchCalendarEvents({
      office_id: officeId,
      start_date: startDateStr,
      end_date: endDateStr,
      per_page: items.length,
    })
      .then((response) => {
        const tempData: Record<string, ICalendarEvent[]> = {}
        Object.keys(response.result.events).forEach((key) => {
          tempData[key] = []
          response.result.events[key].forEach((event) => {
            tempData[key].push({ ...event, is_hidden: false })
          })
        })

        setEvents(tempData)
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const handleClickEvent = (event: ICalendarEvent, eventDateString: string) => {
    setSelectedEvent(event)
    setSelectedEventDate(parse(eventDateString, 'yyyy-MM-dd', new Date()))
    setIsEventDetailOpened(true)
    setIsEventDetailLoading(true)
    setSelectedEventParticipants([])
    fetchCalendarEventParticipants(event.id)
      .then((response) => {
        setSelectedEventParticipants(response)
        setIsEventDetailLoading(false)
      })
  }

  const handleHideShowEvent = (eventId: number, eventDate: Date, isAll: boolean, isHidden: boolean) => {
    const tempEvents = {...events}
    Object.keys(tempEvents).forEach((date) => {
      tempEvents[date] = tempEvents[date].map((event) => {
        if (isAll && event.id === eventId) {
          return {
            ...event,
            is_hidden: isHidden
          }
        }

        if (!isAll && event.id === eventId && format(eventDate, 'yyyy-MM-dd') === date) {
          return {
            ...event,
            is_hidden: isHidden
          }
        }

        return event
      })
    })

    setEvents(tempEvents)
  }

  return (
    <div className="border-[#CBCBCB80] flex-auto overflow-auto">
      <div className="grid grid-cols-7">
        {items.map((item, index) => (
          <div
            key={index}
            className={`h-[192px] flex flex-col border-r border-b border-ui-gray-100 ${isCurrentMonth(item) ? 'bg-white' : 'bg-ui-gray-50'}`}
          >
            <div className={`py-2 text-base text-center ${isCurrentMonth(item) ? 'text-ui-gray-900' : 'text-ui-gray-500'}`}>
              {(isCurrentDate(item)) ? (
                <div className="bg-[#007AFF] text-white w-[32px] h-[32px] rounded-lg flex items-center justify-center m-auto">{item.day}</div>
              ) : (
                <div>{item.day}</div>
              )}
            </div>
            <ul className="overflow-auto">
              {
                // eslint-disable-next-line array-callback-return
                getItemEvents(item).map((event) => {
                  if (!event.is_hidden && event.is_canceled === 'false')
                    return (
                      <li
                        key={`${event.id}:${uuidv4()}`}
                        className="text-sm mt-2 p-1"
                        onClick={() => handleClickEvent(event, `${item.year}-${(item.month + 1).toString().padStart(2, '0')}-${item.day.toString().padStart(2, '0')}`)}
                      >
                        <div
                          className="flex gap-1 p-2 rounded-lg cursor-pointer"
                          style={{ background: getCalendarEventTypeColors(event.event_type).light }}
                        >
                          <div
                            className="mt-[6px] h-2 w-2 rounded-full flex-none"
                            style={{ background: getCalendarEventTypeColors(event.event_type).dark }}
                          />
                          <div>{convert24HourTo12Hour(event.start_at)} {event.title}</div>
                        </div>
                      </li>
                    )
                })
              }
            </ul>
          </div>
        ))}
      </div>
      {selectedEvent && selectedEventDate && (
        <EventDetailModal
          event={selectedEvent}
          eventDate={selectedEventDate}
          participants={selectedEventParticipants}
          loading={isEventDetailLoading}
          open={isEventDetailOpened}
          onDelete={fetchEvents}
          onHideShow={handleHideShowEvent}
          onSuccess={onEventSuccess}
          onClose={() => setIsEventDetailOpened(false)}
        />
      )}
    </div>
  )
}

export default CalendarMonthView
